import { FC, useState } from 'react';
import { type MenuInfo } from 'rc-menu/lib/interface';
import {
  DateRangePicker,
  DateRangeType,
} from '@dynamika-shared/ui/DatePicker/DateRangePicker';
import { Dropdown, MenuItemType } from '@dynamika-shared/ui/Dropdown/Dropdown';
import { Filter, FilledCalendarIcon } from '@dynamika-shared/ui/icons';
import ChevronIcon from '@dynamika-shared/ui/icons/Chevron';
import { MultiFilter } from '@components/NestedFilter/MultiFilter';
import {
  AllFilters,
  SelectedFilters,
  StateFilterItem,
} from '@components/NestedFilter/types';
import { PossibleNotificationHistoryFilters } from '@restore/clinicalTrialNotificationHistory/notificationHistoryFilters.types';
import { RecipientFilterChip } from './RecipientFilterChip';

export type NotificationHistoryFiltersProps = {
  date: DateRangeType;
  onDateChange: (range: DateRangeType) => void;
  subFilters: Array<[PossibleNotificationHistoryFilters, StateFilterItem]>;
  allFilters: AllFilters<PossibleNotificationHistoryFilters, string>;
  selectedFilters: SelectedFilters<PossibleNotificationHistoryFilters>;
  filters: MenuItemType[];
  recipient?: string;
  onRecipientChange: (value: string | undefined) => void;
  onToggleSelectedFilter: (params: {
    name: PossibleNotificationHistoryFilters;
    value: string;
  }) => void;
  onToggleActiveFilter: (key: PossibleNotificationHistoryFilters) => void;
  onFilterClick: (item: MenuInfo) => void;
};

export const NotificationHistoryFilters: FC<NotificationHistoryFiltersProps> = ({
  date,
  onDateChange,
  subFilters,
  allFilters,
  selectedFilters,
  filters,
  recipient,
  onRecipientChange,
  onToggleSelectedFilter,
  onToggleActiveFilter,
  onFilterClick,
}) => {
  const [openDatePicker, setOpenDatePicker] = useState(false);

  return (
    <div className='mb-4 flex w-full flex-row items-center gap-4'>
      <DateRangePicker
        wrapperClassName='w-[275px] min-h-4 flex-shrink-0'
        prefixIcon={<FilledCalendarIcon className='h-[16px] w-[16px]' />}
        className='h-6 w-[290px] pb-[4px] pl-[30px] pr-[11px] pt-[4px] mini:w-[200px]'
        format={'YYYY-LLL-DD'}
        placeholder={['Start date', 'End date']}
        suffixIcon={<ChevronIcon dir='down' />}
        value={date}
        onChange={onDateChange}
        open={openDatePicker}
        onOpenChange={() => setOpenDatePicker(prev => !prev)}
      />
      <div className='ml-auto flex min-w-0 items-center gap-1'>
        <div className='flex min-w-0 items-center gap-1 overflow-x-auto'>
          {subFilters.map(([key, filter]) =>
            key === 'recipient' ? (
              <div
                key={key}
                className='flex-shrink-0 [&_.truncate]:max-w-[30rem]'
              >
                <RecipientFilterChip
                  active={filter.active}
                  filterName={filter.label}
                  value={recipient}
                  onChange={onRecipientChange}
                  onClose={() => onToggleActiveFilter(key)}
                />
              </div>
            ) : (
              <div
                key={key}
                className='flex-shrink-0 [&_.truncate]:max-w-[30rem]'
              >
                <MultiFilter
                  filterName={filter.label}
                  filterKey={key}
                  allItems={allFilters[key] || []}
                  selectedValues={selectedFilters[key] || []}
                  onToggleValue={value =>
                    onToggleSelectedFilter({ name: key, value })
                  }
                  onClose={() => onToggleActiveFilter(key)}
                  active={filter.active}
                  overlayClassName='w-[22.4rem]'
                />
              </div>
            )
          )}
        </div>
        <div
          role='button'
          className='hover:bg-waterLight flex h-[4rem] flex-shrink-0 cursor-pointer items-center rounded-md'
        >
          <Dropdown
            onClick={onFilterClick}
            items={filters}
            trigger={['click']}
            className='h-full p-1'
          >
            <div className='flex items-center justify-center gap-1'>
              <Filter height={20} width={20} />
              <div className='text-1424'>Filter</div>
            </div>
          </Dropdown>
        </div>
      </div>
    </div>
  );
};
